import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from './authService';

const AuthContext = createContext();

// Last user /auth/me confirmed. Used only when /auth/me can't be reached
// (offline, flaky gym signal, a request aborted by navigation), so the app
// keeps working on the stored token instead of signing the user out.
const USER_CACHE_KEY = 'xceller_user';
function readCachedUser() {
  try { return JSON.parse(localStorage.getItem(USER_CACHE_KEY)) || null; } catch { return null; }
}
function cacheUser(user) {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {}
}

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('xceller_token'));
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const isAuthenticated = !!user && !!token;

  const endSession = useCallback(({ expired } = {}) => {
    localStorage.removeItem('xceller_token');
    cacheUser(null);
    setToken(null);
    setUser(null);
    if (expired) setSessionExpired(true);
  }, []);

  /* The token is only checked once, on mount. On a phone the tab lives for
   * days, so a token that expires mid-session leaves isAuthenticated true
   * while every request 401s — which surfaced as a different cryptic error in
   * each feature ("Invalid or expired token" inside the barcode scanner) with
   * no way back. The app calls fetch() directly in several places as well as
   * through apiService, so one interceptor is the only single point that
   * catches them all. */
  useEffect(() => {
    const original = window.fetch;
    window.fetch = async (...args) => {
      const res = await original(...args);
      try {
        const [input] = args;
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        // Our API only, and never the auth endpoints: a 401 from /auth/login
        // is a wrong password, not an expired session.
        if (res.status === 401 && url.includes('/api/') && !url.includes('/api/auth/')) {
          endSession({ expired: true });
        }
      } catch { /* never let the interceptor break a request */ }
      return res;
    };
    return () => { window.fetch = original; };
  }, [endSession]);

  useEffect(() => {
    if (token) {
      authService.getMe(token).then(res => {
        if (res.success) { setUser(res.user); cacheUser(res.user); }
        else endSession({ expired: true });
      }).catch(() => {
        // Network error, not a rejected token: keep the session.
        const cached = readCachedUser();
        if (cached) setUser(cached);
      }).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [token, endSession]);

  const login = async (email, password) => {
    const res = await authService.login(email, password);
    if (res.success) {
      localStorage.setItem('xceller_token', res.token);
      cacheUser(res.user);
      setToken(res.token);
      setUser(res.user);
      setSessionExpired(false);
    }
    return res;
  };

  const register = async (email, password, displayName) => {
    const res = await authService.register(email, password, displayName);
    if (res.success) {
      localStorage.setItem('xceller_token', res.token);
      cacheUser(res.user);
      setToken(res.token);
      setUser(res.user);
    }
    return res;
  };

  const logout = () => endSession();

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isLoading, sessionExpired, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
